import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  PageHeader,
  SectionContainer,
  TableOfContents,
  TOCItem,
  TermsSection
} from './styled';
import {
  Typography,
  Grid,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GavelIcon from '@mui/icons-material/Gavel';

const TermsAndConditionsView = ({ handleClickOnLink }) => {
  const [activeSection, setActiveSection] = useState(null);

  // Table of contents sections
  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'acceptance', title: 'Acceptance of Terms' },
    { id: 'changes', title: 'Changes to Terms' },
    { id: 'accounts', title: 'User Accounts' },
    { id: 'services', title: 'Services and Bookings' },
    { id: 'payments', title: 'Payments and Fees' },
    { id: 'cancellations', title: 'Cancellation Policy' },
    { id: 'conduct', title: 'User Conduct' },
    { id: 'content', title: 'User Content' },
    { id: 'privacy', title: 'Privacy and Data Protection' },
    { id: 'intellectual', title: 'Intellectual Property' },
    { id: 'disclaimers', title: 'Disclaimers' },
    { id: 'limitations', title: 'Limitation of Liability' },
    { id: 'indemnification', title: 'Indemnification' },
    { id: 'termination', title: 'Termination' },
    { id: 'disputes', title: 'Dispute Resolution' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'misc', title: 'Miscellaneous' }
  ];

  const handleScrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Detect active section on scroll
  React.useEffect(() => {
    const handleScroll = () => {

      // Find the section that is currently in view
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { top, bottom } = element.getBoundingClientRect();
          if (top <= 100 && bottom >= 100) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <Container>
      <PageHeader>
        <GavelIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1">
          Terms and Conditions
        </Typography>
        <Typography variant="subtitle1">
          Last Updated: February 25, 2025
        </Typography>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mt: 2 }}
        >
          <Link color="inherit" href="/" onClick={(e) => { e.preventDefault(); handleClickOnLink('/'); }}>
            Home
          </Link>

          <Typography color="text.primary">Terms and Conditions</Typography>
        </Breadcrumbs>
      </PageHeader>

      <SectionContainer>
        <Grid container spacing={4}>
          {/* Table of Contents - Left Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: { md: 'sticky' },
                top: { md: 100 },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'primary.main', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                <Typography variant="subtitle1" fontWeight={600} color="white">
                  Table of Contents
                </Typography>
              </Box>
              <TableOfContents>
                {sections.map((section) => (
                  <TOCItem
                    key={section.id}
                    active={activeSection === section.id}
                    onClick={() => handleScrollToSection(section.id)}
                  >
                    {section.title}
                  </TOCItem>
                ))}
              </TableOfContents>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              {/* Introduction */}
              <TermsSection id="introduction">
                <Typography variant="h5" component="h2" gutterBottom>
                  1. Introduction
                </Typography>
                <Typography variant="body1" paragraph>
                  Welcome to HiveSpace, a platform that connects workspace providers with individuals and businesses seeking flexible workspace solutions. These Terms and Conditions govern your use of the HiveSpace website, mobile applications, and services.
                </Typography>
                <Typography variant="body1" paragraph>
                  By accessing or using our services, you agree to be bound by these Terms and Conditions. Please read them carefully before using HiveSpace.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Definitions */}
              <TermsSection id="definitions">
                <Typography variant="h5" component="h2" gutterBottom>
                  2. Definitions
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="'HiveSpace', 'we', 'us', or 'our'"
                      secondary="Refers to Hive B Ltd, a company registered in England and Wales (Company No. 12345678) with its registered office at 6-8 Bonhill Street, 3rd Floor, London EC2A 4BX, England."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="'Services'"
                      secondary="Refers to the HiveSpace platform, website, mobile applications, and all services provided by HiveSpace."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="'User', 'you', or 'your'"
                      secondary="Refers to any individual or entity using our Services."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="'Host'"
                      secondary="Refers to individuals or businesses offering workspaces through the HiveSpace platform."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="'Guest'"
                      secondary="Refers to individuals or businesses booking workspaces through the HiveSpace platform."
                    />
                  </ListItem>
                </List>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Acceptance of Terms */}
              <TermsSection id="acceptance">
                <Typography variant="h5" component="h2" gutterBottom>
                  3. Acceptance of Terms
                </Typography>
                <Typography variant="body1" paragraph>
                  By accessing or using the Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy.
                </Typography>
                <Typography variant="body1" paragraph>
                  If you are using the Services on behalf of a company or other legal entity, you represent that you have the authority to bind such entity to these Terms and Conditions.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Changes to Terms */}
              <TermsSection id="changes">
                <Typography variant="h5" component="h2" gutterBottom>
                  4. Changes to Terms
                </Typography>
                <Typography variant="body1" paragraph>
                  We reserve the right to modify these Terms and Conditions at any time. We will provide notice of significant changes by posting an update on our website or sending you a notification. Your continued use of the Services after such modifications constitutes your acceptance of the revised Terms and Conditions.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* User Accounts */}
              <TermsSection id="accounts">
                <Typography variant="h5" component="h2" gutterBottom>
                  5. User Accounts
                </Typography>
                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>5.1 Account Creation</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      To use certain features of our Services, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>5.2 Account Security</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>5.3 Account Termination</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      We reserve the right to suspend or terminate your account at any time for any reason, including but not limited to, violation of these Terms and Conditions. You may also request to delete your account at any time by contacting us.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </TermsSection>

              {/* Add more sections following the same pattern */}
              <Divider sx={{ my: 4 }} />

              {/* Services and Bookings */}
              <TermsSection id="services">
                <Typography variant="h5" component="h2" gutterBottom>
                  6. Services and Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                  HiveSpace provides a platform for Hosts to list workspaces and for Guests to discover and book these spaces. We do not own, create, sell, resell, provide, control, manage, or deliver any of the workspaces listed on our platform.
                </Typography>
                <Typography variant="body1" paragraph>
                  When you book a workspace through our platform, you are entering into a contract directly with the Host of that workspace. HiveSpace is not a party to this contract but facilitates the transaction between you and the Host.
                </Typography>
              </TermsSection>

              {/* Payments and Fees */}
              <Divider sx={{ my: 4 }} />

              <TermsSection id="payments">
                <Typography variant="h5" component="h2" gutterBottom>
                  7. Payments and Fees
                </Typography>
                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>7.1 Fee Structure</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      Hosts set their own prices for their workspaces. HiveSpace charges a service fee to both Hosts and Guests for the use of our platform. All applicable fees will be displayed to you before you complete a booking.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>7.2 Payment Processing</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      All payments are processed through our secure payment processor, Stripe. By making a payment through HiveSpace, you agree to Stripe&apos;s terms of service and privacy policy. We do not store your payment information on our servers.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>7.3 Taxes</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      You are responsible for paying all taxes, levies, or duties associated with your use of HiveSpace, including any applicable VAT. Some jurisdictions may require us to collect and remit taxes on your behalf, which will be included in the displayed price.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Cancellation Policy */}
              <TermsSection id="cancellations">
                <Typography variant="h5" component="h2" gutterBottom>
                  8. Cancellation Policy
                </Typography>
                <Typography variant="body1" paragraph>
                  Cancellation policies vary by Host. The specific cancellation policy applicable to your booking will be displayed before you complete the reservation. You agree to abide by the cancellation policy for your specific booking.
                </Typography>
                <Typography variant="body1" paragraph>
                  In general, the following cancellation terms apply unless otherwise specified by the Host:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Full refund"
                      secondary="For cancellations made at least 48 hours before the scheduled start time."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="50% refund"
                      secondary="For cancellations made between 24 and 48 hours before the scheduled start time."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="No refund"
                      secondary="For cancellations made less than 24 hours before the scheduled start time, or for no-shows."
                    />
                  </ListItem>
                </List>
                <Typography variant="body1" paragraph>
                  HiveSpace reserves the right to issue refunds at our discretion, particularly in cases of extenuating circumstances or technical issues with our platform.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* User Conduct */}
              <TermsSection id="conduct">
                <Typography variant="h5" component="h2" gutterBottom>
                  9. User Conduct
                </Typography>
                <Typography variant="body1" paragraph>
                  You agree to use HiveSpace in accordance with all applicable laws and regulations, and you are solely responsible for your conduct while using our Services. You agree not to:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Violate any local, state, national, or international law or regulation" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Infringe upon the rights of any third party, including intellectual property, privacy, and publicity rights" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Use the Services to transmit any material that is harmful, offensive, or objectionable" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Impersonate any person or entity, or falsely state or misrepresent your affiliation with a person or entity" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Use the Services for any purpose other than finding, booking, or providing workspace services" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Attempt to decipher, decompile, disassemble, or reverse engineer any of the software comprising the Services" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Damage, interfere with, or disrupt the operation of the Services or any user's enjoyment of the Services" />
                  </ListItem>
                </List>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* User Content */}
              <TermsSection id="content">
                <Typography variant="h5" component="h2" gutterBottom>
                  10. User Content
                </Typography>
                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>10.1 Content Definition</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                    &quot;User Content&quot; includes any information, text, graphics, photos, or other materials uploaded, posted, or provided by users of the Services, including listings, reviews, and messages.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>10.2 Content Ownership</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      You retain ownership of your User Content. However, by submitting User Content to HiveSpace, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform your User Content in connection with the Services and HiveSpace&apos;s business.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>10.3 Content Guidelines</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      You agree that your User Content will not violate any third-party rights, including copyright, trademark, privacy, or other personal or proprietary rights. You are solely responsible for ensuring your User Content complies with applicable laws and regulations. HiveSpace reserves the right to remove any User Content at our discretion.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Privacy and Data Protection */}
              <TermsSection id="privacy">
                <Typography variant="h5" component="h2" gutterBottom>
                  11. Privacy and Data Protection
                </Typography>
                <Typography variant="body1" paragraph>
                  Your privacy is important to us. Our Privacy Policy, which is incorporated into these Terms and Conditions by reference, explains how we collect, use, and protect your personal information.
                </Typography>
                <Typography variant="body1" paragraph>
                  By using HiveSpace, you consent to our collection and use of your personal information as described in our Privacy Policy. You acknowledge that HiveSpace may process your personal data in countries where data protection laws may be different from those in your country of residence.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Intellectual Property */}
              <TermsSection id="intellectual">
                <Typography variant="h5" component="h2" gutterBottom>
                  12. Intellectual Property
                </Typography>
                <Typography variant="body1" paragraph>
                  The HiveSpace platform, including all content, features, functionality, and user interfaces, is owned by HiveSpace or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                </Typography>
                <Typography variant="body1" paragraph>
                  You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our platform, except as follows:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Your computer may temporarily store copies of materials in RAM incidental to your accessing and viewing those materials." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="You may store files that are automatically cached by your web browser for display enhancement purposes." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="You may print or download one copy of a reasonable number of pages of the website for your own personal, non-commercial use and not for further reproduction, publication, or distribution." />
                  </ListItem>
                </List>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Disclaimers */}
              <TermsSection id="disclaimers">
                <Typography variant="h5" component="h2" gutterBottom>
                  13. Disclaimers
                </Typography>
                <Typography variant="body1" paragraph>
                  THE SERVICES ARE PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. HIVESPACE EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </Typography>
                <Typography variant="body1" paragraph>
                  HIVESPACE DOES NOT GUARANTEE THAT THE SERVICES WILL MEET YOUR REQUIREMENTS, BE AVAILABLE ON AN UNINTERRUPTED, SECURE, OR ERROR-FREE BASIS, OR THAT DEFECTS, IF ANY, WILL BE CORRECTED.
                </Typography>
                <Typography variant="body1" paragraph>
                  HIVESPACE MAKES NO WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR COMPLETENESS OF THE CONTENT ON THE PLATFORM OR THE CONTENT OF ANY WEBSITES LINKED TO THE PLATFORM.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Limitation of Liability */}
              <TermsSection id="limitations">
                <Typography variant="h5" component="h2" gutterBottom>
                  14. Limitation of Liability
                </Typography>
                <Typography variant="body1" paragraph>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, HIVESPACE, ITS AFFILIATES, AND THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Your access to or use of or inability to access or use the Services;" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Any conduct or content of any third party on the Services;" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Any content obtained from the Services; and" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Unauthorized access, use, or alteration of your transmissions or content." />
                  </ListItem>
                </List>
                <Typography variant="body1" paragraph>
                  IN NO EVENT SHALL HIVESPACE&apos;S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT PAID BY YOU TO HIVESPACE, IF ANY, DURING THE PAST SIX MONTHS.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Indemnification */}
              <TermsSection id="indemnification">
                <Typography variant="h5" component="h2" gutterBottom>
                  15. Indemnification
                </Typography>
                <Typography variant="body1" paragraph>
                  You agree to defend, indemnify, and hold harmless HiveSpace, its affiliates, and their respective officers, directors, employees, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys&apos; fees) arising out of or relating to your violation of these Terms and Conditions or your use of the Services.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Termination */}
              <TermsSection id="termination">
                <Typography variant="h5" component="h2" gutterBottom>
                  16. Termination
                </Typography>
                <Typography variant="body1" paragraph>
                  HiveSpace may terminate or suspend your access to all or part of the Services, with or without notice, for any reason, including but not limited to, violation of these Terms and Conditions.
                </Typography>
                <Typography variant="body1" paragraph>
                  You may terminate your account at any time by contacting us at hello@hivespace.co. Upon termination, your right to use the Services will immediately cease.
                </Typography>
                <Typography variant="body1" paragraph>
                  All provisions of these Terms and Conditions that by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Dispute Resolution */}
              <TermsSection id="disputes">
                <Typography variant="h5" component="h2" gutterBottom>
                  17. Dispute Resolution
                </Typography>
                <Typography variant="body1" paragraph>
                  In the event of any dispute arising out of or in connection with these Terms and Conditions or your use of the Services, you and HiveSpace agree to attempt to resolve such dispute through good faith negotiations.
                </Typography>
                <Typography variant="body1" paragraph>
                  If the dispute cannot be resolved through negotiation, both parties agree to resolve the dispute through arbitration in accordance with the rules of the London Court of International Arbitration. The arbitration shall take place in London, England, and shall be conducted in the English language.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Governing Law */}
              <TermsSection id="governing-law">
                <Typography variant="h5" component="h2" gutterBottom>
                  18. Governing Law
                </Typography>
                <Typography variant="body1" paragraph>
                  These Terms and Conditions shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
                </Typography>
                <Typography variant="body1" paragraph>
                  You agree to submit to the personal and exclusive jurisdiction of the courts located in London, England, for the resolution of any disputes arising out of or relating to these Terms and Conditions or your use of the Services.
                </Typography>
              </TermsSection>

              <Divider sx={{ my: 4 }} />

              {/* Miscellaneous */}
              <TermsSection id="misc">
                <Typography variant="h5" component="h2" gutterBottom>
                  19. Miscellaneous
                </Typography>
                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>19.1 Entire Agreement</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      These Terms and Conditions, together with the Privacy Policy and any other legal notices published by HiveSpace on the Services, shall constitute the entire agreement between you and HiveSpace concerning the Services.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>19.2 Waiver</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      No waiver by HiveSpace of any term or condition set out in these Terms and Conditions shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure of HiveSpace to assert a right or provision under these Terms and Conditions shall not constitute a waiver of such right or provision.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>19.3 Severability</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      If any provision of these Terms and Conditions is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of the Terms and Conditions will continue in full force and effect.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={500}>19.4 Contact Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      Questions or comments about the Terms and Conditions should be addressed to:
                    </Typography>
                    <Typography variant="body1">
                      Hive B Ltd<br />
                      6-8 Bonhill Street, 3rd Floor<br />
                      London EC2A 4BX<br />
                      England<br />
                      Email: hello@hivespace.co<br />
                      Phone: +44 7731 917493
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </TermsSection>

            </Paper>
          </Grid>
        </Grid>
      </SectionContainer>
    </Container>
  );
};

TermsAndConditionsView.propTypes = {
  handleClickOnLink: PropTypes.func.isRequired,
  permitedUrls: PropTypes.object,
};

TermsAndConditionsView.defaultProps = {
  permitedUrls: {},
};

export default TermsAndConditionsView;